import { useState, useRef, useEffect, useMemo } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useGetDeviceType } from "~/hooks/useGetDeviceType";
import { QuestionCard } from "../QuestionCard";
import { ActionButtons } from "../ActionButtons";
import { useQuizTracker } from "~/hooks/useQuizTracker";
import { QUIZ_EVENTS, ratingLabel } from "~/utils/analytics/quizEvents";
import * as styles from "./index.module.scss";

const responsive = {
  desktop: {
    breakpoint: { max: 20000, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

// const triggerGAEvent = (rating, currentCard, title) => {
//   const { id, name, label, sublabel } = currentCard;
//   let action = 'click/tap';

//   switch (rating) {
//     case 1:
//       action += ' thumbs up (+1)';
//       break;
//     case -1:
//       action += ' thumbs down (-1)';
//       break;
//     case 0:
//       action += ' Not sure (0)';
//   }

//   // TODO: Migrate to amplitude
//   evQuizRatingAnswered({ property: id, name, rating }, title);
//   gtmNotify(action, 'Quiz v2 rating', { eventLabel: sublabel ? `${label} ${sublabel}` : label });
// };

const findLandingCardPos = (updatedOptions) => {
  // if last one was rated we start from the beginning
  if (updatedOptions[updatedOptions.length - 1].value !== 0) return 0;
  // else we find the last one rated
  const lastRatedCard = updatedOptions
    .slice()
    .reverse()
    .find((card) => card.value !== 0);
  return lastRatedCard ? updatedOptions.indexOf(lastRatedCard) + 1 : 0;
};

function RatingQuestionContent({
  options,
  answerValue,
  quizStepId,
  quizQuestionText,
  onFinish,
  onChangeValue,
  hideNotSureButton = false,
}) {
  const carouselRef = useRef();
  const deviceType = useGetDeviceType();
  const track = useQuizTracker();
  const initialValues = answerValue || null;

  const mutatedOptions = useMemo(() => {
    if (initialValues) {
      return options.map((option) => {
        const currentValue = initialValues.find(
          (value) => value.id === option.id || value.name === option.name,
        );
        return { ...option, value: currentValue?.value || 0 };
      });
    } else {
      return options.map((option) => ({ ...option, value: 0 }));
    }
  }, [initialValues, options]);

  const [currentCard, setCurrentCard] = useState(() =>
    findLandingCardPos(mutatedOptions),
  );

  useEffect(() => {
    // This is not the best nor the ideal solution, but I couldn't find a better way to
    // reset the carousel position when we're doing a client-side transition from
    // one carousel to another one. The component doesn't re-render, thus, we need to
    // "force" the initial position re-calculation and navigate the carousel to the new position.
    if (carouselRef.current) {
      const position = findLandingCardPos(mutatedOptions);
      setCurrentCard(position);
      carouselRef.current.goToSlide(position);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizStepId]);

  const clickCardHandler = (cardIndex) => {
    carouselRef.current.goToSlide(cardIndex);
  };

  const questionCards = useMemo(
    () =>
      mutatedOptions.map((option, index) => {
        const selectedCard = mutatedOptions[currentCard]?.id;
        if (!selectedCard) return null;
        return (
          <QuestionCard
            {...option}
            key={option.id}
            disabled={selectedCard !== option.id}
            index={index}
            onClick={clickCardHandler}
          />
        );
      }),
    [mutatedOptions, currentCard],
  );

  const selectedCard = useMemo(
    () => mutatedOptions[currentCard]?.id,
    [mutatedOptions, currentCard],
  );

  useEffect(() => {
    // If there's no selected card, probably because options changed,
    // but the current card is still in the previous question card index,
    // for some reason the new options don't have cards on that index, then
    // we default to the initial card
    if (!selectedCard) {
      setCurrentCard(0);
    }
  }, [selectedCard, quizStepId]);

  if (!selectedCard) return null;

  /**
   * User answer
   * @param {*} rating positive (1), negative (-1) or neutral (0)
   */
  const onClickButtonActionHandler = (rating) => {
    const lastCardIndex = mutatedOptions.length - 1;

    const updatedOptions = [...mutatedOptions];
    updatedOptions[currentCard].value = rating;

    const answers = updatedOptions.map(({ id, name, value }) => ({
      id,
      name,
      value,
    }));

    onChangeValue(answers);

    track(QUIZ_EVENTS.RATING_ANSWERED, {
      // Legacy contract: property = card id, name = card name, rating = string
      // enum, quiz_question_text = question title (lowercased like the other
      // per-question events). step_id/card_id/card_name kept as additive detail.
      property: updatedOptions[currentCard].id,
      name: updatedOptions[currentCard].name,
      rating: ratingLabel(rating),
      quiz_question_text: quizQuestionText?.toLowerCase(),
      step_id: quizStepId,
      card_id: updatedOptions[currentCard].id,
      card_name: updatedOptions[currentCard].name,
    });

    if (carouselRef.current.state.currentSlide === lastCardIndex) {
      onFinish(false);
      // if last slide, don't do next()
      return;
    }

    // Let the Carousel handle the next card based on the synthetic event triggered by the handler below
    setTimeout(carouselRef.current.next, 100);
  };

  const onSliderChangeHandler = (activeItem) => {
    // This handler is triggered every time we manipulate the Carousel's position through its reference.
    // e.g.: carouselRef.current.next().
    // This should be the only place we mutate the currentCard state.
    setCurrentCard(activeItem);
    if (activeItem === mutatedOptions.length) {
      onFinish(false);
    }
  };

  const currentCardValue = mutatedOptions[currentCard].value;

  return (
    <div
      data-testid="rating-question"
      className={styles.ratingQuestionContainer}
    >
      <div className={styles.cardsContainerEdges}>
        <div className={styles.cardsWrapper}>
          <Carousel
            ref={carouselRef}
            className={styles.cards}
            responsive={responsive}
            itemClass={styles.carouselItem}
            beforeChange={onSliderChangeHandler}
            arrows={false}
            swipeable
            draggable
            ssr
            deviceType={deviceType}
          >
            {questionCards}
          </Carousel>
        </div>
      </div>
      <ActionButtons
        onClick={onClickButtonActionHandler}
        currentRating={currentCardValue}
        hideNotSureButton={hideNotSureButton}
      />
    </div>
  );
}

export function RatingQuestion(props) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);

    return () => {
      setIsClient(false);
    };
  }, []);

  if (!isClient) {
    return <></>;
  }

  return <RatingQuestionContent {...props} />;
}
